/**
 * ToolsHub.jsx — /tools
 * Free Utility Tools hub — no account needed
 *
 * Sub-routes handled here via internal state (no nested router needed):
 *   dev      — Developer utilities (JSON, Base64, JWT, UUID, Hash, Regex, YAML, Diff)
 *   text     — Text tools (Word count, Case convert, Lorem ipsum, HTML encode)
 *   files    — File tools (Image convert, PDF merge, PDF compress, QR code)
 *   network  — Network tools (IP lookup, DNS, WHOIS, Port scan, SSL, Blacklist)
 *
 * Each category links out to the existing pages:
 *   /tools/dev     /tools/text     /tools/files     /tools/network
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(220,230,250,.45)', dim:'rgba(220,230,250,.22)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71',
  orange:'#e67e22', yellow:'#f1c40f', purple:'#b78cff',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
  mono:'"JetBrains Mono",monospace',
};

const CATEGORIES = [
  {
    id:    'dev',
    path:  '/tools/dev',
    icon:  '⌨️',
    color:  T.cyan,
    label: 'Developer Utilities',
    desc:  'JSON, Base64, JWT, UUID, Hash, Regex tester, YAML, Diff',
    tools: [
      { name:'JSON Formatter & Validator', icon:'{}' },
      { name:'Base64 Encode / Decode',     icon:'64' },
      { name:'JWT Decoder',                icon:'🔑' },
      { name:'UUID Generator',             icon:'#'  },
      { name:'SHA-256 / 512 Hash',         icon:'🔐' },
      { name:'Regex Tester',               icon:'.*' },
      { name:'YAML Formatter',             icon:'📄' },
      { name:'Text Diff',                  icon:'⟷'  },
      { name:'URL Encoder / Decoder',      icon:'🔗' },
      { name:'HMAC Generator',             icon:'🛡'  },
    ],
  },
  {
    id:    'text',
    path:  '/tools/text',
    icon:  '📝',
    color:  T.purple,
    label: 'Text Tools',
    desc:  'Word counter, Case converter, Lorem ipsum, HTML encode, Markdown preview',
    tools: [
      { name:'Word Counter',         icon:'📊' },
      { name:'Case Converter',       icon:'Aa' },
      { name:'Remove Duplicates',    icon:'🔄' },
      { name:'Lorem Ipsum',          icon:'📜' },
      { name:'Markdown Preview',     icon:'👁'  },
      { name:'HTML Encoder/Decoder', icon:'<>'  },
      { name:'HTML → Markdown',      icon:'⬇'  },
    ],
  },
  {
    id:    'files',
    path:  '/tools/files',
    icon:  '📁',
    color:  T.orange,
    label: 'File Tools',
    desc:  'Image converter, PDF merger, PDF compressor, QR code generator',
    tools: [
      { name:'Image Converter',   icon:'🖼'  },
      { name:'Image Compressor',  icon:'📦' },
      { name:'Image Resizer',     icon:'↔'  },
      { name:'PDF Merger',        icon:'📑' },
      { name:'PDF Compressor',    icon:'🗜'  },
      { name:'QR Code Generator', icon:'📲' },
    ],
  },
  {
    id:    'network',
    path:  '/tools/network',
    icon:  '🌐',
    color:  T.green,
    label: 'Network Tools',
    desc:  'IP lookup, DNS, WHOIS, Port scanner, SSL checker, Blacklist, Speed test',
    tools: [
      { name:'What Is My IP',       icon:'📍' },
      { name:'IP Lookup',           icon:'🗺'  },
      { name:'DNS Lookup',          icon:'📡' },
      { name:'MX Record Checker',   icon:'✉'  },
      { name:'SSL Certificate',     icon:'🔒' },
      { name:'WHOIS Lookup',        icon:'🔍' },
      { name:'Port Scanner',        icon:'🚪' },
      { name:'Blacklist Check',     icon:'🚫' },
      { name:'HTTP Headers',        icon:'📋' },
      { name:'Speed Test',          icon:'⚡' },
      { name:'SPF Record Checker',  icon:'📧' },
      { name:'Traceroute',          icon:'🛣'  },
    ],
  },
];

export default function ToolsHub() {
  const [hovered, setHovered] = useState(null);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        .tool-chip { transition: background .12s, color .12s, border-color .12s; }
        .tool-chip:hover { background: rgba(255,255,255,.06) !important; color: #dce6fa !important; }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans }}>

        {/* Nav */}
        <nav style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'13px 28px', background:T.bg2,
          borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:10,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <Link to="/" style={{ fontSize:16, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
              Tylent<span style={{ color:T.cyan }}>ed</span>
            </Link>
            <span style={{ color:T.dim }}>›</span>
            <span style={{ fontSize:14, fontWeight:700, color:T.white }}>🛠️ Free Tools</span>
            <span style={{ padding:'2px 8px', borderRadius:20, background:`${T.orange}12`, border:`1px solid ${T.orange}35`, fontSize:10, fontWeight:700, color:T.orange }}>
              Free · No sign-up
            </span>
          </div>
          <div style={{ display:'flex', gap:16 }}>
            {[
              ['/job-search','🎯 Job Search'],
              ['/freelancer','💼 Freelancer'],
              ['/developer','⌨️ Developer'],
              ['/finance','🍁 Finance'],
            ].map(([path, label]) => (
              <Link key={path} to={path} style={{ fontSize:12.5, color:T.muted, textDecoration:'none' }}>
                {label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Hero */}
        <div style={{ padding:'44px 28px 36px', textAlign:'center', borderBottom:`1px solid ${T.border}`, background:`linear-gradient(180deg,rgba(230,126,34,.04) 0%,transparent 100%)` }}>
          <h1 style={{ fontSize:'clamp(1.6rem,3.5vw,2.4rem)', fontWeight:900, color:T.white, letterSpacing:'-.03em', marginBottom:10 }}>
            🛠️ Free Utility Tools
          </h1>
          <p style={{ fontSize:14, color:T.muted, maxWidth:520, margin:'0 auto', lineHeight:1.7 }}>
            40+ browser-based tools — no account, no sign-up, no ads on most tools. Everything runs instantly in your browser.
          </p>
        </div>

        {/* Category cards */}
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'44px 28px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(460px,1fr))', gap:20 }}>
            {CATEGORIES.map(cat => (
              <div
                key={cat.id}
                onMouseEnter={() => setHovered(cat.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  padding:'24px 22px', borderRadius:14,
                  border:`1px solid ${hovered===cat.id ? cat.color+'50' : T.border}`,
                  background: hovered===cat.id ? `${cat.color}06` : 'rgba(255,255,255,.02)',
                  transition:'all .2s',
                  boxShadow: hovered===cat.id ? `0 6px 32px ${cat.color}12` : 'none',
                }}
              >
                {/* Header */}
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:5 }}>
                      <span style={{ fontSize:22 }}>{cat.icon}</span>
                      <span style={{ fontSize:16, fontWeight:800, color:T.white }}>{cat.label}</span>
                    </div>
                    <div style={{ fontSize:12.5, color:T.muted, lineHeight:1.5 }}>{cat.desc}</div>
                  </div>
                  <Link to={cat.path} style={{
                    padding:'7px 14px', borderRadius:7, flexShrink:0, marginLeft:12,
                    border:`1px solid ${cat.color}50`, background:`${cat.color}10`,
                    color:cat.color, fontSize:12.5, fontWeight:700,
                    textDecoration:'none', whiteSpace:'nowrap',
                  }}>
                    Open →
                  </Link>
                </div>

                {/* Tool chips */}
                <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                  {cat.tools.map(tool => (
                    <Link key={tool.name} to={cat.path} className="tool-chip" style={{
                      padding:'4px 10px', borderRadius:20,
                      border:`1px solid ${T.border}`,
                      background:'rgba(255,255,255,.025)',
                      color:T.muted, fontSize:11.5, textDecoration:'none',
                      display:'inline-flex', alignItems:'center', gap:5,
                    }}>
                      <span style={{ fontSize:11 }}>{tool.icon}</span>
                      {tool.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* AI tools upsell */}
          <div style={{
            marginTop:36, padding:'24px 28px', borderRadius:12,
            background:`linear-gradient(135deg, rgba(0,200,240,.06), rgba(77,143,255,.06))`,
            border:`1px solid rgba(0,200,240,.15)`,
            display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:16,
          }}>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:T.white, marginBottom:4 }}>
                🤖 Need AI-powered tools?
              </div>
              <div style={{ fontSize:13, color:T.muted }}>
                Job Search, Freelancer, and Developer portals have 14 AI tools — try free for 7 days.
              </div>
            </div>
            <Link to="/login" style={{
              padding:'9px 20px', borderRadius:8,
              background:`linear-gradient(135deg,${T.cyan},${T.blue})`,
              color:'#07080c', fontSize:13.5, fontWeight:700, textDecoration:'none',
            }}>
              Start free trial →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
